import { css } from '@emotion/react';

import { useProjectStateLifecycle } from '@/Client/Layout/useProjectStateLifecycle';
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { InfoCircleIcon } from '@kaiizen/kai-shiki/react';

// Prototype `.sec-taskcard-co-banner`'s own amber wash — not part of the design token set,
// so pinned to its exact values rather than approximated from an existing token.
const BANNER_BACKGROUND = '#FBF1E3';
const BANNER_BORDER = '#E8D8BE';
const BANNER_TEXT = '#7A5A24';

/**
 * Bleeds to the edges of whatever box it leads (see OVERVIEW_HEADER_BOX_STYLE's own
 * 16px/18px padding) via matching negative margins, so it reads as a strip across the
 * top of the box rather than an inset callout.
 */
const CHANGE_ORDER_BANNER_STYLE = css`
  label: FOCUSED_TASK_CHANGE_ORDER_BANNER;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: -16px -18px 14px;
  padding: 10px 18px;
  background: ${BANNER_BACKGROUND};
  border-bottom: 1px solid ${BANNER_BORDER};
  color: ${BANNER_TEXT};
  font-family: ${FONT.body};
  font-size: ${pxToRem(12.5)};
  line-height: 1.45;
`;

const CHANGE_ORDER_BANNER_ICON_STYLE = css`
  label: FOCUSED_TASK_CHANGE_ORDER_BANNER_ICON;
  flex-shrink: 0;
  margin-top: 1px;
`;

const CHANGE_ORDER_BANNER_BODY_STYLE = css`
  label: FOCUSED_TASK_CHANGE_ORDER_BANNER_BODY;
  flex: 1;
  min-width: 0;

  b {
    color: ${COLORS.ink};
    font-weight: 700;
  }
`;

/**
 * Prototype `.sec-taskcard-co-banner` — leads a task's overview box once the scope is
 * approved (through closeout), warning that further edits are staged as a change order
 * rather than applied to the scope directly.
 */
export const FocusedTaskChangeOrderBanner = () => {
  const projectStateLifecycle = useProjectStateLifecycle();
  const applies = projectStateLifecycle === 'approved' || projectStateLifecycle === 'closeout';
  if (!applies) return null;

  return (
    <div css={CHANGE_ORDER_BANNER_STYLE}>
      <span css={CHANGE_ORDER_BANNER_ICON_STYLE}>
        <InfoCircleIcon size={15} color={BANNER_TEXT} />
      </span>
      <span css={CHANGE_ORDER_BANNER_BODY_STYLE}>
        {'Edits to the contractor, options or products will create a '}
        <b>{'Change Order'}</b>
        {' since the scope is approved.'}
      </span>
    </div>
  );
};
